import { useState, useEffect, useMemo } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/solid";
import carimg from "images/designB/carimg.png";
import { useData } from "data/data-provider";
import { useNavigate } from "react-router-dom";

const CarLogo = () => {
  const [visibleStartIndex, setVisibleStartIndex] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(2);
  const { ourVehicles } = useData();
  const navigate = useNavigate();

  const handleResize = () => {
    if (window.innerWidth >= 1280) {
      setItemsPerPage(6); // xl
    } else if (window.innerWidth >= 1024) {
      setItemsPerPage(4); // lg
    } else if (window.innerWidth >= 768) {
      setItemsPerPage(3); // md
    } else {
      setItemsPerPage(2); // sm
    }
  };

  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const brands = useMemo(() => {
    const seen = new Set<string>();

    // Get unique brands from vehicles
    return ourVehicles
      .filter((vehicle) => vehicle.fleet_class && vehicle.fleet_class.name)
      .map((vehicle) => {
        const name = vehicle.fleet_class?.name?.trim() || "";

        if (seen.has(name.toLowerCase())) return null;
        seen.add(name.toLowerCase());

        return {
          name: name,
          logo: carimg, // Use carimg for all logos
          isDefault: false,
        };
      })
      .filter(Boolean) as { name: string; logo: string; isDefault: boolean }[];
  }, [ourVehicles]);

  const handleNext = () => {
    if (visibleStartIndex + itemsPerPage < brands.length) {
      setVisibleStartIndex(visibleStartIndex + itemsPerPage);
    }
  };

  const handlePrevious = () => {
    if (visibleStartIndex - itemsPerPage >= 0) {
      setVisibleStartIndex(visibleStartIndex - itemsPerPage);
    }
  };
  const showNavigation = useMemo(() => {
    return brands.length > itemsPerPage;
  }, [brands.length, itemsPerPage]);

  return (
    <>
      {showNavigation && (
        <div className="flex flex-row justify-end space-x-2 !mt-4 pr-4">
          <button
            className={`w-10 h-10 flex items-center justify-center rounded-full bg-primary-300 hover:bg-primary-300 ${
              visibleStartIndex === 0 ? "opacity-50 cursor-not-allowed" : ""
            }`}
            aria-label="Previous"
            onClick={handlePrevious}
            disabled={visibleStartIndex === 0}
          >
            <ChevronLeftIcon className="w-6 h-6 text-text-color" />
          </button>
          <button
            className={`w-10 h-10 flex items-center justify-center rounded-full bg-primary-300 hover:bg-primary-300 ${
              visibleStartIndex + itemsPerPage >= brands.length
                ? "opacity-50 cursor-not-allowed"
                : ""
            }`}
            aria-label="Next"
            onClick={handleNext}
            disabled={visibleStartIndex + itemsPerPage >= brands.length}
          >
            <ChevronRightIcon className="w-6 h-6 text-text-color" />
          </button>
        </div>
      )}

      <div className="flex justify-start !mt-6 w-full">
        <div
          className="grid gap-3 sm:gap-6 md:gap-8 lg:gap-12 w-full"
          style={{
            gridTemplateColumns: `repeat(${itemsPerPage}, minmax(0, 1fr))`,
          }}
        >
          {brands
            .slice(visibleStartIndex, visibleStartIndex + itemsPerPage)
            .map((brand, index) => (
              <div
                key={index}
                onClick={() => navigate("/vehicles?make=" + brand.name)}
                className="flex flex-col items-center p-2 sm:p-3 md:p-4 bg-[#FFFFFF0D] border !border-primary-100 rounded-lg shadow hover:shadow-md transition cursor-pointer"
              >
                <img
                  src={carimg} // Using carimg for all logos
                  alt={`${brand.name} car`}
                  className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 lg:w-28 lg:h-28 mb-2 object-contain"
                />
                <span className="text-text-color font-medium text-xs sm:text-sm md:text-base text-center break-words">
                  {brand.name}
                </span>
              </div>
            ))}
        </div>
      </div>
    </>
  );
};

export default CarLogo;
